import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppStore } from "../store/useAppStore";
import { rankSituations } from "../domain/situationEngine";
import { rankingRationale } from "../domain/jarvis";
import { DesktopHeaderBar } from "../components/layout/AppShell";
import { Chip } from "../components/ui/Chip";
import { StateTrack } from "../components/ui/StateTrack";
import { JarvisPanel } from "../components/situations/JarvisPanel";
import { SituationPanel } from "../components/situations/SituationPanel";
import type { Situation } from "../domain/types";

const priorityTone = { critical: "danger", high: "attention", medium: "operational", low: "operational" } as const;

/** lg: breakpoint (1024px) is where Today switches to the two-pane master/detail layout. */
function isMasterDetailViewport() {
  return typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
}

export function Today() {
  const navigate = useNavigate();
  const activeSiteId = useAppStore((s) => s.demo.activeSiteId);
  const situations = useAppStore((s) => s.situations);
  const tours = useAppStore((s) => s.tours);
  const dmName = useAppStore((s) => s.demo.dmName);

  const siteSituations = useMemo(() => situations.filter((s) => s.siteId === activeSiteId), [situations, activeSiteId]);
  const ranked = useMemo(() => rankSituations(siteSituations), [siteSituations]);
  const needsYou = ranked.filter((s) => s.needsAttention);
  const canWait = ranked.filter((s) => !s.needsAttention);
  const hero = needsYou[0] ?? ranked[0] ?? null;
  const deck = ranked.filter((s) => s.id !== hero?.id);

  const scheduled = useMemo(
    () =>
      tours
        .filter((t) => t.siteId === activeSiteId && (t.status === "scheduled" || t.status === "prep"))
        .map((t) => ({ id: t.id, time: t.scheduledAt, title: `Tour — ${t.prospectCompany}`, subtitle: t.useCase, to: `/tour/${t.id}` })),
    [tours, activeSiteId]
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = ranked.find((s) => s.id === selectedId) ?? hero;

  function openSituation(id: string) {
    if (isMasterDetailViewport()) {
      setSelectedId(id);
    } else {
      navigate(`/situations/${id}`);
    }
  }

  return (
    <div className="flex flex-1 flex-col lg:flex-row">
      <div className="flex flex-1 flex-col gap-5 p-4 lg:max-w-[440px] lg:overflow-y-auto lg:border-r lg:border-border">
        <DesktopHeaderBar />
        <div>
          <h1 className="font-display text-[26px] font-black leading-8 tracking-tight text-brand-navy">Monday briefing</h1>
          <p className="mt-0.5 text-[13.5px] text-muted-foreground">
            Good morning, {dmName.split(" ")[0]}. {needsYou.length} situation{needsYou.length === 1 ? "" : "s"} {needsYou.length === 1 ? "needs" : "need"} you today.
          </p>
        </div>

        {hero && (
          <button type="button" onClick={() => openSituation(hero.id)} className="block w-full text-left">
            <HeroCard situation={hero} runnerUp={deck[0] ?? null} selected={selected?.id === hero.id} />
          </button>
        )}

        {needsYou.length > 1 && (
          <Section title="Needs you" count={needsYou.length - 1}>
            {needsYou.slice(1).map((s) => (
              <DeckCard key={s.id} situation={s} onSelect={() => openSituation(s.id)} selected={selected?.id === s.id} />
            ))}
          </Section>
        )}

        {scheduled.length > 0 && (
          <Section title="Scheduled today" count={scheduled.length}>
            <ul className="flex flex-col divide-y divide-border rounded-md border border-border bg-card">
              {scheduled.map((ev) => (
                <li key={ev.id}>
                  <Link to={ev.to} className="flex items-center gap-3 px-3.5 py-3 hover:bg-muted">
                    <time className="w-14 flex-none font-mono text-[12px] font-semibold text-brand-navy">
                      {new Date(ev.time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                    </time>
                    <div className="min-w-0 flex-1">
                      <p className="font-action text-[14px] font-bold text-foreground">{ev.title}</p>
                      <p className="truncate text-[12.5px] text-muted-foreground">{ev.subtitle}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {canWait.length > 0 && (
          <Section title="Can wait" count={canWait.length}>
            <ul className="flex flex-col divide-y divide-border rounded-md border border-border bg-card">
              {canWait.map((s) => (
                <li key={s.id}>
                  <button type="button" onClick={() => openSituation(s.id)} className="flex w-full items-center justify-between gap-2 px-3.5 py-2.5 text-left hover:bg-muted">
                    <span className="text-[13px] font-medium text-foreground">{s.title}</span>
                    <Chip tone={priorityTone[s.priority]}>{s.priority}</Chip>
                  </button>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {ranked.length === 0 && <p className="rounded-md border border-dashed border-border p-6 text-center text-[13.5px] text-muted-foreground">Nothing open at this site right now.</p>}
      </div>

      {/* Tablet/desktop detail pane — master/detail composition, not a stretched phone card. */}
      <div className="hidden flex-1 flex-col overflow-y-auto lg:flex">
        {selected ? (
          <SituationPanel situation={selected} />
        ) : (
          <p className="p-8 text-center text-[13.5px] text-muted-foreground">Select a situation to review.</p>
        )}
      </div>
    </div>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2 px-0.5">
        <h2 className="font-action text-[12px] font-bold uppercase tracking-wide text-muted-foreground">{title}</h2>
        <span className="rounded-full bg-brand-soft-slate px-2 py-0.5 font-mono text-[10.5px] text-brand-slate">{count}</span>
      </div>
      {children}
    </section>
  );
}

function HeroCard({ situation, runnerUp, selected }: { situation: Situation; runnerUp: Situation | null; selected: boolean }) {
  return (
    <div className={`overflow-hidden rounded-lg bg-brand-navy text-white shadow-elevation-2 ${selected ? "ring-2 ring-primary ring-offset-2" : ""}`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <span className="font-action text-[11px] font-bold uppercase tracking-wide text-[#7FB7E4]">Start here</span>
          <Chip tone="danger">{situation.priority}</Chip>
        </div>
        <h3 className="mt-2 font-display text-[19px] font-black leading-6">{situation.title}</h3>
        <p className="mt-1 text-[13px] leading-5 text-navyFgMuted">{situation.summary}</p>
        {situation.workflow && (
          <div className="mt-3.5">
            <StateTrack steps={situation.workflow.steps} compact />
          </div>
        )}
      </div>
      <div className="border-t border-navyBorder bg-navy2 p-3.5">
        <JarvisPanel label="Jarvis · ranking" onDark>
          <p className="text-[12.5px]">{rankingRationale(situation, runnerUp)}</p>
        </JarvisPanel>
      </div>
    </div>
  );
}

function DeckCard({ situation, onSelect, selected }: { situation: Situation; onSelect: () => void; selected: boolean }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full flex-col gap-2 rounded-md border bg-card p-3.5 text-left shadow-elevation-1 ${
        selected ? "border-primary ring-1 ring-primary" : "border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-action text-[15px] font-bold leading-5 text-foreground">{situation.title}</h3>
        <Chip tone={priorityTone[situation.priority]}>{situation.priority}</Chip>
      </div>
      <p className="text-[13px] leading-5 text-muted-foreground">{situation.summary}</p>
      {situation.workflow && <StateTrack steps={situation.workflow.steps} compact />}
    </button>
  );
}
